import { forwardRef, useId, useRef, useState, type ButtonHTMLAttributes } from 'react';
import { X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import './canvas-action-help.css';

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  actionLabel: string;
  description: string;
  onHelpOpenChange?: (open: boolean) => void;
};

/** Mouse/keyboard get a tooltip; touch gets guidance before the existing action. */
export const CanvasActionButton = forwardRef<HTMLButtonElement, Props>(function CanvasActionButton(
  { actionLabel, description, onHelpOpenChange, onClick, onPointerDown, className = '', children, ...props }, ref,
) {
  const [helpOpen, setHelpOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const pointerType = useRef('');
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const runningAction = useRef(false);
  const headingId = useId();
  const descriptionId = useId();
  const changeHelp = (open: boolean) => { if (open) runningAction.current = false; setHelpOpen(open); onHelpOpenChange?.(open); };
  return <Popover open={helpOpen} onOpenChange={changeHelp}>
    <Tooltip delayDuration={250} open={!helpOpen && tooltipOpen} onOpenChange={open => setTooltipOpen(open && pointerType.current !== 'touch' && pointerType.current !== 'pen')}>
      <PopoverAnchor asChild>
        <TooltipTrigger asChild>
          <button {...props} ref={node => { buttonRef.current = node; if (typeof ref === 'function') ref(node); else if (ref) ref.current = node; }} type="button" aria-label={actionLabel}
            className={`canvas-action-icon ${className}`}
            onPointerEnter={event => { pointerType.current = event.pointerType; props.onPointerEnter?.(event); }}
            onPointerDown={event => { pointerType.current = event.pointerType; if (event.pointerType !== 'mouse') setTooltipOpen(false); onPointerDown?.(event); }}
            onKeyDown={event => { pointerType.current = ''; props.onKeyDown?.(event); }}
            onClick={event => {
              if (pointerType.current === 'touch' || pointerType.current === 'pen') {
                event.preventDefault(); event.stopPropagation(); setTooltipOpen(false); changeHelp(true);
                return;
              }
              setTooltipOpen(false); onClick?.(event);
            }}>
            {children}
          </button>
        </TooltipTrigger>
      </PopoverAnchor>
      <TooltipContent side="bottom" sideOffset={8} collisionPadding={12} className="canvas-action-tooltip">
        <strong>{actionLabel}</strong><span>{description}</span>
      </TooltipContent>
    </Tooltip>
    <PopoverContent side="bottom" align="end" sideOffset={10} collisionPadding={12}
      className="canvas-action-touch-help nodrag nowheel" data-canvas-action-help
      aria-labelledby={headingId} aria-describedby={descriptionId}
      onCloseAutoFocus={event => { event.preventDefault(); if (!runningAction.current) buttonRef.current?.focus(); }}
      onPointerDown={event => event.stopPropagation()} onClick={event => event.stopPropagation()}>
      <div className="canvas-action-help-heading">
        <h3 id={headingId}>{actionLabel}</h3>
        <button type="button" aria-label="Close action help" onClick={() => changeHelp(false)}><X size={16} /></button>
      </div>
      <p id={descriptionId}>{description}</p>
      <button type="button" className="canvas-action-help-continue" disabled={props.disabled}
        onClick={event => { runningAction.current = true; changeHelp(false); onClick?.(event); }}>{actionLabel}</button>
    </PopoverContent>
  </Popover>;
});
